import { Button } from "@/app/global-components/button";
import LanguageSelector from "@/components/language-selector";
import { routes } from "@/consts";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import { useLocale } from "@/middleware";
import { RootState } from "@/store/type";
import { LANG, ROLE } from "@/types";
import {
  Badge,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Tooltip,
  Typography,
} from "@mui/material";
import { isUndefined } from "lodash";
import { AddCircle, Delete, RemoveCircle } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { CiMenuBurger } from "react-icons/ci";
import { FaShoppingCart, FaWhatsapp } from "react-icons/fa";
import { IoMdNotificationsOutline } from "react-icons/io";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SidebarView from "../sidebar-view";
import useNotification from "@/hooks/use-notification";
import { Box } from "@mui/system";
import { useTranslate } from "@/locales";

function TopSide({
  children,
  navMenu,
  marketPlaceList,
  openSideBar,
}: {
  openSideBar: boolean;

  children: React.ReactNode;
  marketPlaceList?: {
    title: string;
    path: string;
    icon: JSX.Element;
    header: string;
  }[];
  navMenu: {
    title: string;
    path: string;
    show: boolean;
    icon: JSX.Element;
    header: string;
  }[];
}) {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const { locale } = useLocale();
  const location = useLocation();
  const toggleSideBar = () => {
    setSidebarOpen((prev) => !prev);
  };
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotification();

  console.log(notifications, unreadCount);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = 0;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const swipeDistance =
      locale === LANG.EN
        ? touchEndX.current - touchStartX.current
        : touchStartX.current - touchEndX.current;

    if (swipeDistance > 150 && touchEndX.current > 50) {
      toggleSideBar();
    }
  };
  const [anchor2El, setAnchor2El] = useState<null | HTMLElement>(null);
  const [anchor3El, setAnchor3El] = useState<null | HTMLElement>(null);

  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const handleNotificationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchor3El(event.currentTarget);
  };
  const toggleCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor2El(event.currentTarget);
  };
  const handleWhatsAppClick = () => {
    const phoneNumber = "966530975200";
    const whatsappUrl = `https://wa.me/${phoneNumber}`;
    window.open(whatsappUrl, "_blank");
  };
  const cartOpen = Boolean(anchor2El);
  const notificationCenterOpen = Boolean(anchor3El);
  const handleNotificationClose = () => {
    setAnchor3El(null);
  };

  const { t } = useTranslate(locale);
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const navigate = useNavigate();

  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity >= 1) {
      updateQuantity(id, quantity);
    }
  };

  const handleRemoveItem = (id: string) => {
    removeFromCart(id);
  };

  const handleCheckout = () => {
    navigate(routes.checkout);
  };
  const handleCartClose = () => {
    setAnchor2El(null);
  };
  return (
    <div className="h-full w-full overflow-hidden">
      {/* Modern Top Header */}
      <div
        className={cn(
          "bg-gradient-to-r from-white via-gray-50 to-white fixed z-10 border-b border-gray-200 h-20 w-full backdrop-blur-sm",
          openSideBar ? "md:w-[calc(100vw-288px)]" : "md:w-[calc(100vw-80px)]"
        )}
        style={{
          background:
            "linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)",
        }}
      >
        {/* Desktop Header */}
        <div className="px-6 md:flex hidden items-center h-full justify-between">
          {/* Page Title */}
          <div className="flex items-center gap-4">
            <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-blue-600 rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {navMenu.find((item) => item.path == location.pathname)?.header}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {t("general.welcomeBack")} {authUser?.nickName}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <div className="bg-white rounded-xl p-1">
              <LanguageSelector />
            </div>

            {/* WhatsApp Support */}
            <Tooltip title="WhatsApp Support" arrow>
              <IconButton
                onClick={handleWhatsAppClick}
                className="text-green-600 hover:text-green-700 transition-colors duration-200"
                sx={{
                  width: 48,
                  height: 48,
                }}
              >
                <FaWhatsapp size={20} />
              </IconButton>
            </Tooltip>

            {/* Shopping Cart */}
            {userRole === ROLE.BUYER && (
              <Tooltip title="Shopping Cart" arrow>
                <IconButton
                  onClick={toggleCart}
                  className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                  sx={{
                    width: 48,
                    height: 48,
                  }}
                >
                  <Badge
                    badgeContent={cartItems.length}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#ef4444",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <FaShoppingCart size={20} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Notifications */}
            <Tooltip title="Notifications" arrow>
              <IconButton
                onClick={handleNotificationClick}
                className="text-orange-600 hover:text-orange-700 transition-colors duration-200"
                sx={{
                  width: 48,
                  height: 48,
                }}
              >
                <Badge
                  color="error"
                  badgeContent={unreadCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#ef4444",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <IoMdNotificationsOutline size={20} />
                </Badge>
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="h-full md:hidden flex items-center justify-between px-4">
          {/* Mobile Logo */}
          <Link
            className="flex items-center text-textcolor-black cursor-pointer"
            to={routes.home}
          >
            <h1 className="text-2xl font-bold text-gray-900">
              Med<span>ex</span>
            </h1>
          </Link>

          {/* Mobile Actions */}
          <div className="flex items-center gap-2">
            {userRole === ROLE.BUYER && (
              <IconButton
                onClick={toggleCart}
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                sx={{ width: 40, height: 40 }}
              >
                <Badge badgeContent={cartItems.length} color="error">
                  <FaShoppingCart size={18} />
                </Badge>
              </IconButton>
            )}

            <div className="bg-white rounded-lg">
              <LanguageSelector />
            </div>

            <IconButton
              onClick={handleNotificationClick}
              className="text-orange-600 hover:text-orange-700 transition-colors duration-200"
              sx={{ width: 40, height: 40 }}
            >
              <Badge color="error" badgeContent={unreadCount}>
                <IoMdNotificationsOutline size={18} />
              </Badge>
            </IconButton>

            {/* Mobile Menu */}
            <div>
              <SidebarView
                marketPlaceList={marketPlaceList}
                list={navMenu.filter((item) => item.show)}
                sidebarOpen={sidebarOpen}
                toggleSidebar={toggleSideBar}
              />
              <Button
                className="outline-none text-gray-700 border-0 bg-transparent h-10 px-3 rounded-lg transition-all duration-200 md:hidden hover:bg-gray-100"
                onClick={toggleSideBar}
              >
                <CiMenuBurger className="text-xl" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        dir={locale === LANG.AR ? "rtl" : "ltr"}
        onTouchEnd={handleTouchEnd}
        className="md:h-[calc(100vh-80px)] md:overflow-x-hidden md:overflow-y-auto md:no-scrollbar mt-20 bg-gradient-to-br from-gray-50 via-white to-gray-50"
        style={{
          background:
            "linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)",
        }}
      >
        {children}
      </div>

      {/* Shopping Cart Menu */}
      <Menu
        anchorEl={anchor2El}
        open={cartOpen}
        onClose={handleCartClose}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            overflowY: "auto",
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-t-3xl">
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "1.1rem",
            }}
          >
            🛒 {t("navigation.ShoppingCart")}
          </Typography>
        </div>

        <List sx={{ p: 0 }}>
          {cartItems.length > 0 ? (
            cartItems.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  borderBottom: "1px solid #f3f4f6",
                  padding: "16px",
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                {/* Item Image */}
                {item.images?.[0] ? (
                  <Box
                    component="img"
                    src={item.images[0]}
                    alt={item.name}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      border: "2px solid #e5e7eb",
                      objectFit: "cover",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      border: "2px solid #e5e7eb",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#f3f4f6",
                      color: "#6b7280",
                      fontSize: "0.75rem",
                    }}
                  >
                    {t("images.noImage")}
                  </Box>
                )}

                {/* Item Details */}
                <Box sx={{ flexGrow: 1 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#1f2937" }}
                      >
                        {item.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{ color: "#6b7280", mt: 0.5 }}
                      >
                        {t("item.fields.price")}:{" "}
                        <span className="font-semibold text-purple-600">
                          {item.price.toFixed(2)} {t("currency")}
                        </span>
                      </Typography>
                    }
                    sx={{
                      textAlign: locale === LANG.AR ? "right" : "left",
                      direction: "rtl",
                    }}
                  />

                  {/* Quantity Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      marginTop: "8px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: locale === LANG.AR ? "right" : "left",
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        color: "#6b7280",
                        fontSize: "0.75rem",
                      }}
                    >
                      {t("checkout.fields.quantity")}:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(item.id, item.quantity - 1)
                      }
                      sx={{
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        "&:hover": {
                          backgroundColor: "#e5e7eb",
                          color: "#374151",
                        },
                      }}
                    >
                      <RemoveCircle fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#1f2937",
                        minWidth: "20px",
                        textAlign: "center",
                      }}
                    >
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(item.id, item.quantity + 1)
                      }
                      sx={{
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        "&:hover": {
                          backgroundColor: "#e5e7eb",
                          color: "#374151",
                        },
                      }}
                    >
                      <AddCircle fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Remove Item */}
                <IconButton
                  onClick={() => handleRemoveItem(item.id)}
                  sx={{
                    color: "#ef4444",
                    backgroundColor: "#fef2f2",
                    "&:hover": {
                      backgroundColor: "#fee2e2",
                      color: "#dc2626",
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItem>
            ))
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <div className="text-6xl mb-4">🛒</div>
              <Typography
                variant="body1"
                sx={{
                  color: "#6b7280",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {t("checkout.messages.emptyCart")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#9ca3af",
                }}
              >
                Add some items to get started
              </Typography>
            </Box>
          )}
        </List>

        {cartItems.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Button
                onClick={handleCheckout}
                disabled={cartItems.length === 0}
              >
                {t("navigation.Checkout")} ({cartItems.length} items)
              </Button>
            </Box>
          </>
        )}
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={anchor3El}
        open={notificationCenterOpen}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            overflowY: "auto",
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
            >
              🔔 {t("navigation.Notifications")}
            </Typography>
            {notifications.some((notify) => !notify.isRead) && (
              <Tooltip title={t("actions.markAllAsRead")} arrow>
                <IconButton
                  onClick={markAllAsRead}
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        <List sx={{ p: 0 }}>
          {notifications.length > 0 ? (
            notifications.map((notification) => {
              if (notification.data) {
                return (
                  <ListItem
                    key={notification.data.id}
                    sx={{
                      backgroundColor: notification.data.isRead
                        ? "#f9fafb"
                        : "#ffffff",
                      borderBottom: "1px solid #f3f4f6",
                      padding: "16px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: notification.data.isRead
                          ? "#f3f4f6"
                          : "#fef3c7",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.data.isRead ? 400 : 600,
                            color: notification.data.isRead
                              ? "#6b7280"
                              : "#1f2937",
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.data.message}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#9ca3af",
                            mt: 0.5,
                            display: "block",
                          }}
                        >
                          {new Date(
                            notification.data.createdAt
                          ).toLocaleString()}
                        </Typography>
                      }
                      sx={{
                        textAlign: locale === LANG.AR ? "right" : "left",
                        direction: "rtl",
                      }}
                    />
                    {!notification.data.isRead && (
                      <Tooltip title={t("actions.markAsRead")} arrow>
                        <IconButton
                          onClick={() => markAsRead(notification.data.id)}
                          sx={{
                            color: "#644980",
                            backgroundColor: "#eff6ff",
                            "&:hover": {
                              backgroundColor: "#dbeafe",
                              color: "#644980",
                            },
                          }}
                        >
                          <DoneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItem>
                );
              }
              return (
                <ListItem
                  key={notification.id}
                  sx={{
                    backgroundColor: notification.isRead
                      ? "#f9fafb"
                      : "#ffffff",
                    borderBottom: "1px solid #f3f4f6",
                    padding: "16px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: notification.isRead
                        ? "#f3f4f6"
                        : "#fef3c7",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: notification.isRead ? 400 : 600,
                          color: notification.isRead ? "#6b7280" : "#1f2937",
                          lineHeight: 1.4,
                        }}
                      >
                        {notification.message}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#9ca3af",
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                    }
                    sx={{
                      textAlign: locale === LANG.AR ? "right" : "left",
                      direction: "rtl",
                    }}
                  />
                  {!notification.isRead && (
                    <Tooltip title={t("actions.markAsRead")} arrow>
                      <IconButton
                        onClick={() => markAsRead(notification.id)}
                        sx={{
                          color: "#644980",
                          backgroundColor: "#eff6ff",
                          "&:hover": {
                            backgroundColor: "#dbeafe",
                            color: "#644980",
                          },
                        }}
                      >
                        <DoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
              );
            })
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <div className="text-6xl mb-4">🔔</div>
              <Typography
                variant="body1"
                sx={{
                  color: "#6b7280",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {t("notifications.noAvailable")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#9ca3af",
                }}
              >
                {t("notifications.allCaughtUp")}
              </Typography>
            </Box>
          )}
        </List>
      </Menu>
    </div>
  );
}

export default TopSide;
